import { Translation } from 'cng-common/features/translations'
import {
  getAttentionSeverityColor,
  getAttentionSeverityLevels,
} from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { AttentionCount } from '../components/attentionCount'
import { AttentionNextStepsList } from '../components/attentionNextSteps'
import { AttentionRobotFigure } from '../components/attentionRobotFigure'
import { AttentionStatusCard } from '../components/attentionStatusCard'
import { AttentionStatusesEmptyState } from '../components/attentionStatusesEmptyState'
import { useAttentionStatusesText } from '../hooks/useAttentionStatusesText'
import { countAttentionRaises } from '../lib'
import type { IAttentionStatusesVariantProps } from '../types'

import styles from './AttentionStatusesHero.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

/** The level the cards badge as `Top Priority`. */
const TOP_SEVERITY = 3

export const AttentionStatusesHero = ({
  items,
  totalCount,
  unit,
  levelSeverity,
  severityCounts: givenSeverityCounts,
  onSelectItem,
  nextSteps,
  onOpenPartnerDetail,
}: IAttentionStatusesVariantProps) => {
  const text = useAttentionStatusesText()

  const shownCount = countAttentionRaises(items)

  /* Nothing raised is one state across the three layouts, so it is one stub: a hero counting `0`
     says the same thing in a shape built to show what is wrong. */
  if (items.length === 0) {
    return <AttentionStatusesEmptyState totalCount={totalCount} unit={unit} />
  }

  /* Only a level that knows its own size has rows it is not showing; the status level counts what
     the payload raised and has nothing to subtract from. */
  const restCount = totalCount === undefined ? 0 : totalCount - shownCount
  const severityCounts =
    givenSeverityCounts ??
    getAttentionSeverityLevels(items).map((severity) => ({
      severity,
      count: countAttentionRaises(items.filter((item) => item.severity === severity)),
    }))

  return (
    <div className={styles.body}>
      <div className={styles.heroWrap}>
        {/* The robot says how urgent the worst of it is; the split under it counts it. */}
        <div className={styles.heroFigure}>
          <AttentionRobotFigure severity={levelSeverity ?? severityCounts[0].severity} />
        </div>
        <div className={styles.heroText}>
          {/* Inside a status every card shares its level, so the worst level is said once, under
              the robot, instead of badged over each card. */}
          {levelSeverity === TOP_SEVERITY && (
            <span className={styles.topBadge}>
              <span aria-hidden="true">🔥</span> <Translation span text={KEYS.topPriority} />
            </span>
          )}
          <span className={styles.heroLabel}>
            {totalCount === undefined ? (
              /* The split under the line counts each level, so the line names what was counted:
                 the unit's own plural and the state it is in, each its own key. */
              <>
                <Translation span text={unit.pluralKey} /> <Translation span text={unit.stateKey} />
              </>
            ) : (
              /* The robot carries no number, so the line says the count against the whole. */
              <Translation span {...text.phrase.fullCounter(unit, shownCount, totalCount)} />
            )}
          </span>
          <span className={styles.heroSplit}>
            {severityCounts.map(({ severity, count }) => (
              <span key={severity}>
                {/* A line of the split is told apart by colour: the swatch is the level, and the
                    words beside it are its count and the level's name. */}
                <span
                  className={styles.heroSwatch}
                  style={{ background: getAttentionSeverityColor(severity) }}
                  aria-hidden="true"
                />
                <AttentionCount count={count} unit={unit} />
                <span className={styles.heroSeverityName}>
                  <Translation span {...text.phrase.severityName(severity)} />
                </span>
              </span>
            ))}
          </span>
        </div>
      </div>

      <div className={styles.rowsWrap}>
        {/* The statuses list what to do next under each line; the levels the manager cockpit's
          drill-down walks into keep their cards. */}
        {nextSteps ? (
          <AttentionNextStepsList
            items={items}
            onSelectItem={onSelectItem}
            scope={nextSteps}
            onOpenPartnerDetail={onOpenPartnerDetail}
          />
        ) : (
          <div className={styles.rows}>
            {items.map((item) => (
              <AttentionStatusCard
                key={item.id}
                item={item}
                showTopBadge={levelSeverity === undefined}
                onSelect={onSelectItem}
              />
            ))}
          </div>
        )}

        {/* Only the status level has rows it is not showing; a geo or partner level lists all of
            its own, so the footer would say `0 … not affected`. */}
        {restCount > 0 && (
          <div className={styles.footer}>
            <Translation span {...text.phrase.rest(unit, restCount)} />
          </div>
        )}
      </div>
    </div>
  )
}

AttentionStatusesHero.displayName = 'AttentionStatusesHero'
